<template>
  <div class="performance-goals-table" v-loading="loading">
    <div class="table-header">
      <span class="unit-tip">单位：万</span>
      <el-button icon="el-icon-arrow-left" circle size="mini" @click="prevMonth"></el-button>
      <span class="title">{{ title }}</span>
      <el-button icon="el-icon-arrow-right" circle size="mini" @click="nextMonth"></el-button>
    </div>
    <div class="table-content">
      <ve-table
        max-height="100%"
        rowKeyFieldName="user_id"
        :columns="tableColumns"
        :table-data="tableData"
        :footer-data="footerTableData"
        :editOption="editOption"
        border-y
        scrollWidth="100%"
        :row-style-option="rowStyleOption"
        :fixed-header="true"
        :fixed-footer="true"
      />
    </div>
  </div>
</template>

<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import _ from 'lodash';
import { Message } from 'element-ui';
import moment from 'moment';

function formatNumber(val) {
  const num = Number(val);
  return num ? `${num.toLocaleString()}w` : val;
}

const StrongText = {
  functional: true,
  render(h, { props }) {
    return <strong style="color: red;">{props.text}</strong>;
  }
}

@Component
export default class WeekPerformanceGoals extends Vue {
  @Prop({ type: Array, default: () => [] }) users;
  @Prop(Boolean) disabled;

  currentMonth = moment();
  tableData = [];
  loading = false;
  weeks = [];
  editingCells = {}; // 存储正在编辑的单元格集合 { 'userId-weekIndex': { userId, weekIndex, value, originalValue } }


  created() {
    // 在created钩子中立即初始化weeks数组
    this.weeks = this.getWeeksInMonth();
    // 不立即调用init，等users数据准备好后再调用
  }

  // 监听users数组变化，当有用户数据时初始化表格
  @Watch('users', { immediate: true })
  onUsersChange() {
    if (this.users && this.users.length > 0) {
      this.init();
    }
  }

  get tableColumns() {
    return [
      {
        key: 'screen_name',
        field: 'screen_name',
        title: '业务员/周',
        align: 'center',
        width: 200,
        fixed: 'left',
        renderFooterCell({ row }, h) {
          return h(StrongText, {
            props: {
              text: row.screen_name,
            }
          });
        }
      },
      ...(this.weeks || []).map((week, index) => ({
        key: `${index}`,
        field: `${index}`,
        title: `第${index + 1}周\n(${week.start.format('MM-DD')}~${week.end.format('MM-DD')})`,
        align: 'center',
        edit: true,
        width: 150,
        renderBodyCell: ({ row }, h) => {
          const cellKey = `${row.user_id}-${index}`;
          const editingCell = this.editingCells[cellKey];

          // 只有当值存在且与原始值不相等时，才显示保存和取消按钮
          const shouldShowButtons = editingCell &&
            editingCell.value !== '' &&
            editingCell.value !== null &&
            editingCell.value !== undefined &&
            editingCell.value !== editingCell.originalValue;

          if (shouldShowButtons) {
            return h('div', { class: 'cell-with-save' }, [
              h('span', formatNumber(editingCell.value)),
              h('el-button', {
                props: {
                  type: 'primary',
                  size: 'mini',
                  icon: 'el-icon-check',
                  loading: row.saving
                },
                style: {
                  marginLeft: '5px'
                },
                on: {
                  click: () => this.saveCell(row, index)
                }
              }),
              h('el-button', {
                props: {
                  size: 'mini',
                  icon: 'el-icon-close'
                },
                style: {
                  marginLeft: '5px'
                },
                on: {
                  click: () => this.cancelEdit(row.user_id, index)
                }
              })
            ]);
          }

          return formatNumber(row[`${index}`]);
        },
        renderFooterCell({ row }, h) {
          return h(StrongText, {
            props: {
              text: formatNumber(row[`${index}`]),
            }
          });
        }
      })),
      {
        key: 'total',
        field: 'total',
        title: '总计',
        align: 'center',
        width: 80,
        fixed: 'right',
        renderBodyCell: ({ row }) => {
          return formatNumber(_.sum((this.weeks || []).map((week, index) => Number(row[`${index}`]) || 0)));
        },
        renderFooterCell({ row }, h) {
          return h(StrongText, {
            props: {
              text: formatNumber(row.total),
            }
          });
        }
      },
    ];
  }


  rowStyleOption = {
    stripe: false,
    clickHighlight: false,
    hoverHighlight: false,
  }

  get editOption() {
    return {
      beforeStartCellEditing: ({ column }) => {
        if (this.disabled) {
          return false;
        }

        const fieldNum = parseInt(column.field);
        if (isNaN(fieldNum) || fieldNum < 0) {
          return false;
        }

        return true;
      },
      beforeCellValueChange: ({ changeValue, column, row }) => {
        const weekIndex = parseInt(column.field);
        const cellKey = `${row.user_id}-${weekIndex}`;

        // 在值改变之前，保存原始值（如果还没有保存的话）
        if (!this.editingCells[cellKey]) {
          this.$set(this.editingCells, cellKey, {
            userId: row.user_id,
            weekIndex,
            value: row[column.field], // 暂存当前值
            originalValue: row[column.field] // 保存原始值
          });
        }

        // 允许空值
        if (changeValue === '' || changeValue === null || changeValue === undefined) {
          const prevValue = row[column.field];
          return prevValue !== '';
        }

        const num = Number(changeValue);
        if (num < 0 || Number.isNaN(num)) {
          Message.error('请输入大于或等于0的数字');
          return false;
        }
        const prevValue = row[column.field];
        return num !== prevValue;
      },
      afterCellValueChange: ({ row, column, changeValue }) => {
        const weekIndex = parseInt(column.field);

        // 处理空值情况
        let value = 0;
        if (changeValue !== '' && changeValue !== null && changeValue !== undefined) {
          value = Number(changeValue);
        }

        // 更新编辑状态中的新值，但保持originalValue不变
        const cellKey = `${row.user_id}-${weekIndex}`;
        if (this.editingCells[cellKey]) {
          this.$set(this.editingCells[cellKey], 'value', value);

          // 如果值为空或等于原始值，清除编辑状态
          if ((value === '' || value === null || value === undefined || value === 0) ||
              value === this.editingCells[cellKey].originalValue) {
            this.$delete(this.editingCells, cellKey);
          }
        }
      }
    }
  }

  get footerTableData() {
    const weekTotalMap = {};
    (this.weeks || []).forEach((week, index) => {
      weekTotalMap[`${index}`] = 0;
    });

    this.tableData.forEach(row => {
      (this.weeks || []).forEach((week, index) => {
        const key = `${index}`;
        weekTotalMap[key] += Number(row[key]) || 0;
      });
    });

    const total = _.sum(Object.values(weekTotalMap));

    return [
      {
        user_id: -1,
        screen_name: '总计',
        ...weekTotalMap,
        total,
      }
    ]
  }

  get title() {
    return `${this.currentMonth.format('YYYY年MM月')}下单金额目标`;
  }

  getWeeksInMonth() {
    const startOfMonth = this.currentMonth.clone().startOf('month');
    const endOfMonth = this.currentMonth.clone().endOf('month');
    const weeks = [];

    // 获取当月第一天是周几（0=周日，1=周一...6=周六）
    const firstDayOfWeek = startOfMonth.day();

    // 计算第一周到周日的天数
    // 如果1号是周日(0)，则a=0；如果是周一(1)，则a=6；以此类推
    const daysToSunday = firstDayOfWeek === 0 ? 0 : 7 - firstDayOfWeek;

    let weekStart = 1; // 当前周的开始日期（月份中的第几天）

    while (weekStart <= endOfMonth.date()) {
      let weekEnd;

      if (weeks.length === 0) {
        // 第一周：从1号到该周的周日
        weekEnd = weekStart + daysToSunday;
      } else {
        // 后续周：每周7天
        weekEnd = weekStart + 6;
      }

      // 确保不超过月末
      if (weekEnd > endOfMonth.date()) {
        weekEnd = endOfMonth.date();
      }

      weeks.push({
        start: startOfMonth.clone().date(weekStart),
        end: startOfMonth.clone().date(weekEnd)
      });

      // 下一周的开始日期
      weekStart = weekEnd + 1;
    }

    return weeks;
  }

  async fetchData() {
    try {
      const startOfMonth = this.currentMonth.clone().startOf('month');
      const endOfMonth = this.currentMonth.clone().endOf('month');

      const params = {
        start_on_gteq: startOfMonth.format('YYYY-MM-DD'),
        start_on_lteq: endOfMonth.format('YYYY-MM-DD'),
        goal_type_eq: 'weekly',
        page: 0,
      }

      const data = await this.$request.get('/performance_goals', params);

      const groupedData = _.mapValues(
        _.groupBy(data, 'user_id'),
        group => _.groupBy(group, item => {
          // 找到这个日期属于当月的第几周
          const itemDate = moment(item.start_on);
          const weekIndex = this.weeks.findIndex(week =>
            itemDate.isSameOrAfter(week.start, 'day') && itemDate.isSameOrBefore(week.end, 'day')
          );
          return weekIndex;
        })
      );

      return groupedData;
    } catch (error) {
      return {};
    }
  }


  prevMonth() {
    this.currentMonth = this.currentMonth.clone().subtract(1, 'month');
  }

  nextMonth() {
    this.currentMonth = this.currentMonth.clone().add(1, 'month');
  }


  @Watch('currentMonth')
  async init() {
    this.loading = true;
    try {
      // 更新weeks数组
      this.weeks = this.getWeeksInMonth();
      const userData = await this.fetchData();

      this.tableData = this.users.map(user => {
        const row = {
          user_id: user.id,
          screen_name: user.screen_name,
          saving: false,
        }

        this.weeks.forEach((week, index) => {
          const data = _.get(userData, `${user.id}.${index}.0`); // 取第一条记录
          const value = Number(_.get(data, 'sales_target')); // 开单金额
          Object.assign(row, {
            [`${index}`]: Number.isNaN(value) || value === 0 ? '' : value / 10000, // 用于单元格编辑，空值或0显示为空字符串
            [`data_week_${index}`]: data || null, // 保存performance_goal用于判断是新增还是更新
          });
        });

        return row;
      });
    } finally {
      this.loading = false;
    }
  }


  async saveCell(row, weekIndex) {
    const cellKey = `${row.user_id}-${weekIndex}`;
    const editingCell = this.editingCells[cellKey];
    if (!editingCell) return;

    const value = editingCell.value;
    this.$delete(this.editingCells, cellKey); // 清除编辑状态

    await this.handleSave(row, weekIndex, value);
  }

  cancelEdit(userId, weekIndex) {
    const cellKey = `${userId}-${weekIndex}`;
    const editingCell = this.editingCells[cellKey];
    if (!editingCell) return;

    // 恢复原始值
    const row = this.tableData.find(r => r.user_id === userId);
    if (row) {
      this.$set(row, `${weekIndex}`, editingCell.originalValue);
    }

    this.$delete(this.editingCells, cellKey);
  }

  async handleSave(row, weekIndex, value) {
    Object.assign(row, {
      [`${weekIndex}`]: value,
      saving: true,
    });


    try {
      const week = this.weeks[weekIndex];
      const body = {
        start_on: week.start.format('YYYY-MM-DD'),
        user_id: row.user_id,
        goal_type: 'weekly',
        sales_target: value * 10000
      }

      let updateData = null;
      if (row[`data_week_${weekIndex}`]) {
        // 更新现有记录
        updateData = await this.$request.put(`/performance_goals/${row[`data_week_${weekIndex}`].id}`, body);
      } else {
        // 创建新记录
        updateData = await this.$request.post('/performance_goals', body);
      }

      row[`data_week_${weekIndex}`] = updateData;
      Message.success('保存成功');

    } catch (error) {
      Message.error('保存失败，请重试');

      // 恢复到之前的值
      const prevValue = Number(_.get(row[`data_week_${weekIndex}`], 'sales_target'));
      row[`${weekIndex}`] = prevValue ? prevValue / 10000 : '';

    } finally {
      row.saving = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.performance-goals-table {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-content {
  flex: 1;
  overflow: hidden;
}

.vue-table-root,
:deep(.ve-table) {
  height: 100%;
}

.table-header {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: $primary;
  color: #fff;
  padding: 5px 10px;
  position: relative;

  .unit-tip {
    position: absolute;
    left: 10px;
    color: #ffd700;
    background: rgba(255, 255, 255, 0.1);
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: bold;
  }

  .title {
    margin: 0 10px;
  }
}

:deep(.cell-with-save) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}
</style>
